import { useEffect, useRef } from 'react'
import { useBlocker } from 'react-router-dom'
import { Modal } from 'antd'
import { useTranslation } from 'react-i18next'

/**
 * 當 dirtyCount > 0 時：
 *   - 瀏覽器級離開（關分頁 / 重刷 / 上一頁）：跳 beforeunload 原生確認框
 *   - 站內導航（React Router）：跳 Modal
 *       [先儲存再離開] → 呼叫 onSaveAndLeave()；resolve true 才放行，false 留在頁
 *       [取消]         → 留在頁
 *
 * 為何只有兩顆按鈕：AntD Modal.confirm 原生只支援兩顆。使用者想「丟棄並離開」的
 * 路徑，改由 StickyActionBar 的「放棄變更」按鈕實現（按下後 dirty=0，導航就不會被攔）。
 *
 * Caller 合約：onSaveAndLeave 若失敗（resolve false 或 throw），須自行向使用者顯示
 * 錯誤訊息（例如 message.error / notification）。hook 內 catch 區段只做 console.error
 * 與 blocker.reset()，不會代為彈出 toast——避免與 caller 自己的錯誤訊息形成雙重訊號。
 *
 * @param {number} dirtyCount                     用於在 Modal content 中顯示筆數
 * @param {() => Promise<boolean>} [onSaveAndLeave] 回傳 true 才放行；false 則阻擋並留在頁。
 *                                                未提供時「先儲存再離開」等同取消。
 */
export function useLeaveProtection(dirtyCount, onSaveAndLeave) {
  const { t } = useTranslation()
  const onSaveRef = useRef(onSaveAndLeave)
  onSaveRef.current = onSaveAndLeave

  // Modal.confirm 開出後 content 已凍結；dirtyCount 走 ref 避免放進 effect deps —
  // saveAll 成功清空 dirty 後若觸發 effect 重跑，會在 blocker 仍 blocked 時開出第二個 Modal。
  const dirtyCountRef = useRef(dirtyCount)
  dirtyCountRef.current = dirtyCount

  const isDirty = dirtyCount > 0

  useEffect(() => {
    if (!isDirty) return
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    isDirty && currentLocation.pathname !== nextLocation.pathname
  )

  // 防止 StrictMode 雙跑 effect / 並發 render 在同一個 blocked 狀態下重複開窗。
  const modalOpenRef = useRef(false)

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      modalOpenRef.current = false
      return
    }
    if (modalOpenRef.current) return
    modalOpenRef.current = true

    Modal.confirm({
      title: t('shared.stock_takings.show.spreadsheet.leave_title'),
      content: t('shared.stock_takings.show.spreadsheet.leave_content', {
        count: dirtyCountRef.current
      }),
      okText: t('shared.stock_takings.show.spreadsheet.leave_save_and_go'),
      cancelText: t('shared.stock_takings.show.spreadsheet.leave_cancel'),
      okButtonProps: { type: 'primary' },
      onOk: async () => {
        try {
          const ok = await onSaveRef.current?.()
          if (ok) {
            blocker.proceed()
          } else {
            blocker.reset()
          }
        } catch (err) {
          console.error('[useLeaveProtection] save-and-leave failed:', err)
          // proceed() 已執行後 blocker 已轉為 unblocked，對其再呼叫 reset 會 throw。
          if (blocker.state === 'blocked') {
            blocker.reset()
          }
        }
      },
      onCancel: () => blocker.reset()
    })
  }, [blocker, t])
}
